import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode
} from 'react';
import type { AxiosError } from 'axios';
import api from './api.js';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
    _retryCount?: number;
  }
  interface InternalAxiosRequestConfig {
    skipAuthRedirect?: boolean;
    _retryCount?: number;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface AxiosError<T = unknown, D = any> {
    userMessage?: string;
  }
}

export interface AuthUser {
  id?: number | string;
  accountNumber?: string;
  account_number?: string;
  missionControlAlias?: string | null;
  mission_control_alias?: string | null;
  hubsAlias?: string | null;
  hubs_alias?: string | null;
  statusTier?: string | null;
  status_tier?: string | null;
  email?: string;
  username?: string;
  name?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  fullName?: string;
  full_name?: string;
  profileImage?: string;
  profile_image?: string;
  company?: string;
  role?: string;
  user_role?: string;
  userRole?: string;
  isRoot?: boolean;
  companyId?: number;
  company_id?: number;
  activeCompanyId?: number;
  mustChangePassword?: boolean;
  must_change_password?: boolean;
  force_password_change?: boolean;
  [key: string]: unknown;
}

export interface MembershipFeature {
  featureKey: string;
  label?: string;
  description?: string;
  [key: string]: unknown;
}

export interface Membership {
  id?: number | string;
  status?: string;
  planCode?: string;
  planName?: string;
  startsAt?: string;
  endsAt?: string;
  renewsAt?: string;
  seatsPurchased?: number;
  seatsConsumed?: number;
  features?: MembershipFeature[];
  [key: string]: unknown;
}

export type MembershipType = 'guest' | string;

export interface AuthResult {
  success: boolean;
  error?: string;
  forcePasswordChange?: boolean;
  requiresMfa?: boolean;
  mfaToken?: string;
  maskedPhone?: string | null;
  delivery?: string;
  type?: string;
  data?: unknown;
  token?: string;
  redirectToSignup?: boolean;
  email?: string;
}

export interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  companyId?: number | string;
  registrationToken?: string;
  inviteToken?: string;
}

interface AnalyticsContext {
  userId?: number | string;
  role?: string | null;
  email?: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  membership: Membership | null;
  membershipType: MembershipType;
  membershipLoaded: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  verifyMfaLogin: (mfaToken: string, code: string) => Promise<AuthResult>;
  resendMfaCode: (mfaToken: string) => Promise<{ success: boolean; error?: string; maskedPhone?: string | null }>;
  loginWithPasskey: (userData: Partial<AuthUser>) => Promise<AuthResult>;
  logout: () => Promise<{ success: boolean; error?: string }>;
  signup: (userData: SignupData) => Promise<AuthResult>;
  checkAuth: () => Promise<void>;
  refreshMembership: (overrideUser?: AuthUser | null) => Promise<Membership | null>;
  markPasswordChanged: () => void;
}

// Analytics will be provided by consuming app
let setDefaultAnalyticsContext: (ctx?: AnalyticsContext) => void = () => {};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Strip placeholder "User Name" values — mirrors backend utils/normalize-name.js
 */
function sanitizePlaceholderName(first: string, last: string): { firstName: string; lastName: string; fullName: string } {
  let firstName = first.trim();
  let lastName = last.trim();
  if (/^user$/i.test(firstName) && /^name$/i.test(lastName)) {
    firstName = '';
    lastName = '';
  }
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return { firstName, lastName, fullName };
}

function normalizeUser(userData: Partial<AuthUser> & Record<string, unknown>): AuthUser {
  const rawFirst = String(
    (userData.firstName as string) || (userData.first_name as string) || ''
  );
  const rawLast = String(
    (userData.lastName as string) || (userData.last_name as string) || ''
  );

  const { firstName, lastName, fullName: fullNameFromParts } = sanitizePlaceholderName(rawFirst, rawLast);

  let fullName = String(
    (userData.fullName as string) ||
      (userData.full_name as string) ||
      fullNameFromParts ||
      (userData.name as string) ||
      ''
  ).trim();
  if (/^user\s+name$/i.test(fullName) && !fullNameFromParts) {
    fullName = '';
  }

  const profileImg = String(
    (userData.profileImage as string) ||
      (userData.profile_image as string) ||
      ''
  ).trim();

  const rawCompanyId =
    userData.activeCompanyId ?? userData.company_id ?? userData.companyId;
  let companyId: number | undefined;
  if (rawCompanyId != null && String(rawCompanyId) !== '' && !Number.isNaN(Number(rawCompanyId))) {
    companyId = Number(rawCompanyId);
  }

  return {
    ...userData,
    first_name: firstName,
    last_name: lastName,
    firstName,
    lastName,
    fullName,
    profileImage: profileImg,
    profile_image: profileImg,
    company_id: companyId,
    companyId,
    activeCompanyId: companyId,
    mustChangePassword:
      (userData.mustChangePassword as boolean | undefined) ??
      (userData.must_change_password as boolean | undefined) ??
      (userData.force_password_change as boolean | undefined) ??
      false
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  console.log('🚀 AuthProvider: Initializing...');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [membershipLoaded, setMembershipLoaded] = useState<boolean>(false);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);
  const [membershipType, setMembershipType] = useState<MembershipType>('guest');

  const deriveFallbackMembershipType = useCallback(
    (currentUser: AuthUser | null, membershipData: Membership | null): MembershipType => {
      if (!currentUser) {
        return 'guest';
      }
      const role = currentUser.role || currentUser.user_role || currentUser.userRole;
      if (role === 'ADMINISTRATOR_USER' || role === 'SUPER_USER') {
        return 'admin';
      }
      if (membershipData?.planCode) {
        return (membershipData.planCode || '').toLowerCase();
      }
      if (role === 'BUSINESS_USER') {
        return 'commercial_pending';
      }
      return 'guest';
    },
    []
  );

  const loadMembership = useCallback(
    async (overrideUser: AuthUser | null = null): Promise<Membership | null> => {
      try {
        console.log('🚀 AuthProvider: Fetching membership for current user...');
        const response = await api.get('/membership/me');
        const payload = (response.data?.data ?? null) as
          | (Membership & { membership?: Membership; membershipType?: string })
          | null;
        const currentUser = overrideUser || user;

        let membershipData: Membership | null = null;
        let resolvedType: MembershipType = 'guest';

        if (
          payload &&
          typeof payload === 'object' &&
          ('membership' in payload || 'membershipType' in payload)
        ) {
          membershipData = payload.membership ?? null;
          resolvedType =
            payload.membershipType ?? deriveFallbackMembershipType(currentUser, membershipData);
        } else {
          membershipData = payload;
          resolvedType = deriveFallbackMembershipType(currentUser, membershipData);
        }

        setMembership(membershipData);
        setMembershipType(resolvedType);
        return membershipData;
      } catch (err) {
        const axErr = err as AxiosError;
        if (axErr.response?.status === 404) {
          console.log('ℹ️ AuthProvider: No active membership for user');
          setMembership(null);
          const currentUser = overrideUser || user;
          setMembershipType(deriveFallbackMembershipType(currentUser, null));
          return null;
        }
        if (axErr.response?.status === 401) {
          console.log('ℹ️ AuthProvider: Not authenticated when fetching membership');
          setMembership(null);
          setMembershipType('guest');
          return null;
        }
        console.error('❌ AuthProvider: Error loading membership', err);
        setMembership(null);
        setMembershipType('guest');
        return null;
      } finally {
        setMembershipLoaded(true);
      }
    },
    [deriveFallbackMembershipType, user]
  );

  const checkAuth = async (): Promise<void> => {
    console.log('🚀 AuthProvider: checkAuth called...');
    try {
      console.log('🚀 AuthProvider: Making API call to /auth/profile...');
      console.log('🚀 AuthProvider: Current user state:', !!user);
      console.log('🚀 AuthProvider: Current pathname:', window.location.pathname);
      // Mark this request to skip auth redirect - we'll handle 401s ourselves
      const response = await api.get('/auth/profile', {
        skipAuthRedirect: true
      });
      console.log('✅ AuthProvider: API response received:', response.data);
      // Handle different response structures
      const userData = (response.data?.data || response.data) as
        | (Partial<AuthUser> & Record<string, unknown>)
        | null;

      // Normalize user data to always have both snake_case and camelCase
      if (userData && (userData.id || userData.email)) {
        const normalizedUser = normalizeUser(userData);

        console.log('✅ AuthProvider: Setting normalized user:', normalizedUser);
        console.log('🔐 AuthProvider: mustChangePassword check:', {
          mustChangePassword: normalizedUser.mustChangePassword,
          must_change_password: normalizedUser.must_change_password,
          force_password_change: normalizedUser.force_password_change,
          finalValue: Boolean(normalizedUser.mustChangePassword)
        });
        setUser(normalizedUser);
        const shouldChangePassword = Boolean(normalizedUser.mustChangePassword);
        setMustChangePassword(shouldChangePassword);
        if (shouldChangePassword) {
          console.warn(
            '⚠️ AuthProvider: User must change password - this will redirect to /change-password'
          );
        }
        setError(null);
        await loadMembership(normalizedUser);
      } else {
        console.log('⚠️ AuthProvider: No valid user data in response');
        // Don't clear user if we're on change-password page and mustChangePassword is true
        // This prevents redirect loop when checkAuth fails after login
        if (!mustChangePassword) {
          setUser(null);
          setMembership(null);
          setMembershipType('guest');
          setMustChangePassword(false);
        }
        setMembershipLoaded(true);
      }
      console.log('✅ AuthProvider: User set, loading false');
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string | { message?: string } }>;
      console.log('⚠️ AuthProvider: API call failed:', axErr.message, axErr.response?.status);
      // Only keep user state if we're on change-password page AND mustChangePassword is true
      // This prevents redirect loop when checkAuth fails after login, but only on the change-password page
      const isOnChangePasswordPage = window.location.pathname === '/change-password';
      if (axErr.response?.status === 401 && mustChangePassword && isOnChangePasswordPage) {
        console.log(
          '⚠️ AuthProvider: Auth check failed but on change-password page - keeping user state temporarily'
        );
        setLoading(false);
        return;
      }
      // Clear everything when user loses access (401) - they should go to login, not change-password
      setUser(null);
      setMembership(null);
      setMembershipType('guest');
      setMustChangePassword(false);
      setMembershipLoaded(true);
      // Only log non-auth errors (401 is expected when not logged in)
      if (axErr.response?.status !== 401) {
        // Network errors shouldn't break the app
        if (!axErr.response) {
          console.warn('⚠️ Cannot connect to backend for auth check:', axErr.message);
          console.warn('⚠️ This is OK if backend is not running - app will still work');
        } else {
          const errBody = axErr.response?.data?.error;
          const errMessage =
            typeof errBody === 'string'
              ? errBody
              : errBody?.message || axErr.message;
          setError(errMessage);
        }
      }
    } finally {
      console.log('✅ AuthProvider: Setting loading to false');
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🚀 AuthProvider: useEffect triggered, calling checkAuth...');
    // Don't block render - let checkAuth run asynchronously
    checkAuth().catch((caughtError: Error) => {
      console.error('❌ AuthProvider: Error in checkAuth on mount:', caughtError);
      setLoading(false);
      setError(caughtError.message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.id) {
      // Set analytics context with user info - this ensures all future events have userId
      // Analytics is optional - only set if available
      if (typeof setDefaultAnalyticsContext === 'function') {
        setDefaultAnalyticsContext({
          userId: user.id,
          role: user.role || user.user_role || user.userRole || null,
          email: user.email || null
        });
      }
    } else {
      // Clear analytics context when user logs out
      if (typeof setDefaultAnalyticsContext === 'function') {
        setDefaultAnalyticsContext({});
      }
    }
  }, [user]);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.data?.requiresMfa && response.data?.mfaToken) {
        return {
          success: true,
          requiresMfa: true,
          mfaToken: response.data.mfaToken,
          maskedPhone: response.data.maskedPhone ?? null,
          delivery: response.data.delivery,
        };
      }

      // Handle different response formats
      const responseData = (response.data?.data || response.data) as
        | (Partial<AuthUser> & Record<string, unknown>)
        | null;

      // Update user state immediately with login response data (includes role)
      // BUT ONLY IF IT IS NOT A COMPANY LOGIN (Companies have their own context)
      if (
        response.data?.type !== 'company' &&
        responseData &&
        (responseData.id || responseData.email)
      ) {
        const normalizedUser = normalizeUser(responseData);

        console.log('Setting user from login response (normalized):', normalizedUser);
        setUser(normalizedUser);
        setMustChangePassword(Boolean(normalizedUser.mustChangePassword));
        setLoading(false);
        await loadMembership(normalizedUser);
      }

      // Don't call checkAuth() after login - we already have the user data from the login response
      // Calling checkAuth() can cause race conditions and clear the user state if it fails

      return {
        success: true,
        forcePasswordChange: Boolean(
          responseData?.mustChangePassword || responseData?.must_change_password
        ),
        type: response.data?.type,
        data: responseData,
        token: response.data?.token
      };
    } catch (err) {
      const axErr = err as AxiosError<
        | {
            error?:
              | string
              | {
                  message?: string;
                  redirectToSignup?: boolean;
                  email?: string;
                };
            message?: string;
          }
        | string
      >;
      console.error('Login error:', err);
      console.error('Error response:', axErr.response?.data);

      // Check for unknown email - redirect to signup
      const errorData =
        typeof axErr.response?.data === 'object' ? axErr.response?.data?.error : undefined;
      if (typeof errorData === 'object' && errorData?.redirectToSignup) {
        console.log(
          '🔀 Unknown email detected, should redirect to signup:',
          errorData.email
        );
        return {
          success: false,
          error: errorData.message || 'Email not found',
          redirectToSignup: true,
          email: errorData.email
        };
      }

      // Extract error message from various possible formats
      let errorMessage = 'Login failed';

      // Check for network errors first (no response from server)
      if (!axErr.response) {
        if (axErr.userMessage) {
          errorMessage = axErr.userMessage;
        } else if (
          axErr.code === 'ECONNREFUSED' ||
          axErr.code === 'ERR_NETWORK' ||
          axErr.message?.includes('Network Error')
        ) {
          errorMessage =
            'Cannot connect to server. Please check if the backend is running on port 3001.';
        } else if (axErr.code === 'ECONNABORTED' || axErr.message?.includes('timeout')) {
          errorMessage = 'Request timeout. Please try again.';
        } else if (axErr.message) {
          errorMessage = axErr.message;
        } else {
          errorMessage = 'Network error occurred. Please check your connection.';
        }
      } else if (axErr.response?.data) {
        const data = axErr.response.data;
        // Server responded with an error
        if (typeof data === 'object' && data.error) {
          const errField = data.error;
          if (typeof errField === 'object') {
            errorMessage = errField.message || errorMessage;
          } else if (typeof errField === 'string') {
            errorMessage = errField;
          }
        } else if (typeof data === 'object' && data.message) {
          errorMessage = data.message;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (axErr.message) {
        errorMessage = axErr.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const verifyMfaLogin = async (mfaToken: string, code: string): Promise<AuthResult> => {
    try {
      const response = await api.post('/auth/mfa/verify', { mfaToken, code });
      const responseData = (response.data?.data || response.data) as
        | (Partial<AuthUser> & Record<string, unknown>)
        | null;

      if (responseData && (responseData.id || responseData.email)) {
        const normalizedUser = normalizeUser(responseData);
        setUser(normalizedUser);
        setMustChangePassword(Boolean(normalizedUser.mustChangePassword));
        setLoading(false);
        await loadMembership(normalizedUser);
      }

      return {
        success: true,
        forcePasswordChange: Boolean(
          responseData?.mustChangePassword || responseData?.must_change_password
        ),
        data: responseData,
      };
    } catch (err) {
      const axErr = err as AxiosError<{ error?: { message?: string } | string; message?: string }>;
      const raw = axErr.response?.data?.error;
      const errorMessage =
        (typeof raw === 'object' && raw?.message) ||
        (typeof raw === 'string' ? raw : undefined) ||
        axErr.response?.data?.message ||
        'Verification failed';
      return { success: false, error: errorMessage };
    }
  };

  const resendMfaCode = async (mfaToken: string) => {
    try {
      const response = await api.post('/auth/mfa/resend', { mfaToken });
      return {
        success: true,
        maskedPhone: response.data?.data?.maskedPhone ?? null,
      };
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string | { message?: string }; message?: string }>;
      const raw = axErr.response?.data?.error;
      return {
        success: false,
        error:
          (typeof raw === 'object' && raw?.message) ||
          (typeof raw === 'string' ? raw : undefined) ||
          axErr.response?.data?.message ||
          'Failed to resend code',
      };
    }
  };

  const loginWithPasskey = async (userData: Partial<AuthUser>): Promise<AuthResult> => {
    try {
      // Normalize user data to always have both snake_case and camelCase
      if (userData && (userData.id || userData.email)) {
        const normalizedUser = normalizeUser(userData as Partial<AuthUser> & Record<string, unknown>);

        console.log('Setting user from passkey login response (normalized):', normalizedUser);
        setUser(normalizedUser);
        setMustChangePassword(Boolean(normalizedUser.mustChangePassword));
        setLoading(false);
        await loadMembership(normalizedUser);

        return {
          success: true,
          forcePasswordChange: Boolean(normalizedUser.mustChangePassword)
        };
      } else {
        throw new Error('Invalid user data from passkey authentication');
      }
    } catch (err) {
      const caughtError = err as Error;
      console.error('Passkey login error:', err);
      return {
        success: false,
        error: caughtError.message || 'Failed to complete passkey login'
      };
    }
  };

  const logout = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      await api.get('/auth/logout');
      setUser(null);
      setMembership(null);
      setMembershipType('guest');
      setMembershipLoaded(false);
      setMustChangePassword(false);
      return { success: true };
    } catch (err) {
      const caughtError = err as Error;
      return { success: false, error: caughtError.message };
    }
  };

  const markPasswordChanged = useCallback((): void => {
    setMustChangePassword(false);
    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        mustChangePassword: false,
        must_change_password: false,
        force_password_change: false
      };
    });
  }, []);

  const signup = async (userData: SignupData): Promise<AuthResult> => {
    try {
      // Map frontend role values to backend role constants
      const roleMap: Record<string, string> = {
        retail_user: 'MARKETPLACE_RETAIL_USER',
        marketplace_retail_user: 'MARKETPLACE_RETAIL_USER',
        business_user: 'BUSINESS_USER',
        messaging_user: 'MESSAGING_USER'
      };

      const normalizedRoleKey = userData.role ? userData.role.trim().toLowerCase() : '';
      const backendRole =
        roleMap[normalizedRoleKey] || 'MARKETPLACE_RETAIL_USER';

      console.log('Attempting signup with:', {
        email: userData.email,
        role: backendRole,
        first_name: userData.firstName,
        last_name: userData.lastName
      });

      const response = await api.post('/user/signup', {
        email: userData.email,
        password: userData.password,
        confirm_password: userData.password,
        role: backendRole,
        first_name: userData.firstName.toLowerCase(),
        last_name: userData.lastName.toLowerCase(),
        companyId: userData.companyId,
        registrationToken: userData.registrationToken,
        inviteToken: userData.inviteToken
      });

      if (response.data?.success) {
        return { success: true };
      }

      return { success: false, error: response.data?.message || 'Signup failed' };
    } catch (err) {
      const axErr = err as AxiosError<{
        error?:
          | string
          | {
              message?: string;
              details?: {
                body?: Array<{ message?: string; path?: string[] }>;
                params?: Array<{ message?: string; path?: string[] }>;
                query?: Array<{ message?: string; path?: string[] }>;
              };
            };
        message?: string;
        details?:
          | Array<{ message?: string }>
          | { body?: Array<{ message?: string }> };
      }>;
      // Enhanced error logging for network issues
      console.error('=== SIGNUP ERROR DETAILS ===');
      console.error('Error type:', axErr.name);
      console.error('Error message:', axErr.message);
      console.error('Error code:', axErr.code);
      console.error('Error response status:', axErr.response?.status);
      console.error('Error response data:', JSON.stringify(axErr.response?.data, null, 2));
      console.error('Error request config:', {
        url: axErr.config?.url,
        baseURL: axErr.config?.baseURL,
        method: axErr.config?.method
      });
      console.error('Full error object:', err);
      console.error('==========================');

      // Handle network errors
      if (!axErr.response) {
        // Network error - no response from server
        if (
          axErr.code === 'ECONNREFUSED' ||
          axErr.code === 'ERR_NETWORK' ||
          axErr.message?.includes('Network Error')
        ) {
          return {
            success: false,
            error: `Cannot connect to server. Please check if the backend is running on ${api.defaults.baseURL}`
          };
        }
        if (axErr.code === 'ERR_CONNECTION_REFUSED') {
          return {
            success: false,
            error: 'Connection refused. Is the backend server running on port 3000?'
          };
        }
        return {
          success: false,
          error: `Network error: ${
            axErr.message || 'Failed to connect to server. Please check your connection and ensure the backend is running.'
          }`
        };
      }

      // Server responded with error
      const data = axErr.response.data;
      let errorMessage = 'Signup failed. Please check your information and try again.';

      // Check for express-validation error format (has details object with body/params/query arrays)
      if (
        data &&
        typeof data === 'object' &&
        'error' in data &&
        typeof data.error === 'object' &&
        data.error?.details
      ) {
        const details = data.error.details;
        const validationMessages: string[] = [];

        // Extract validation messages from body, params, or query
        if (details.body && Array.isArray(details.body)) {
          details.body.forEach((errDetail) => {
            validationMessages.push(
              errDetail.message || `${errDetail.path?.join('.') ?? ''}: invalid`
            );
          });
        }
        if (details.params && Array.isArray(details.params)) {
          details.params.forEach((errDetail) => {
            validationMessages.push(
              errDetail.message || `Parameter ${errDetail.path?.join('.') ?? ''}: invalid`
            );
          });
        }
        if (details.query && Array.isArray(details.query)) {
          details.query.forEach((errDetail) => {
            validationMessages.push(
              errDetail.message || `Query ${errDetail.path?.join('.') ?? ''}: invalid`
            );
          });
        }

        if (validationMessages.length > 0) {
          errorMessage = validationMessages.join('. ');
        } else if (data.error.message) {
          errorMessage = data.error.message;
        }
      }
      // Check standard error format
      else if (
        data &&
        typeof data === 'object' &&
        'error' in data &&
        typeof data.error === 'object' &&
        data.error?.message
      ) {
        errorMessage = data.error.message;
      }
      // Check for validation error details (alternative format)
      else if (data && typeof data === 'object' && 'details' in data && data.details) {
        const d = data.details;
        if (Array.isArray(d)) {
          errorMessage = d.map((entry) => entry.message || String(entry)).join(', ');
        } else if (d.body && Array.isArray(d.body)) {
          errorMessage = d.body.map((entry) => entry.message || '').join(', ');
        }
      }
      // Check for message field
      else if (data && typeof data === 'object' && 'message' in data && data.message) {
        errorMessage = data.message;
      }
      // Check for error string
      else if (
        data &&
        typeof data === 'object' &&
        'error' in data &&
        typeof data.error === 'string'
      ) {
        errorMessage = data.error;
      }
      // Check if data is a string
      else if (typeof data === 'string') {
        errorMessage = data;
      }

      return { success: false, error: errorMessage };
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    error,
    membership,
    membershipType,
    membershipLoaded,
    mustChangePassword,
    login,
    verifyMfaLogin,
    resendMfaCode,
    loginWithPasskey,
    logout,
    signup,
    checkAuth,
    refreshMembership: loadMembership,
    markPasswordChanged
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    // Return a default context instead of throwing - this handles initialization edge cases
    console.warn('useAuth called outside AuthProvider - returning default context');
    return {
      user: null,
      loading: true,
      error: null,
      membership: null,
      membershipType: 'guest',
      membershipLoaded: false,
      mustChangePassword: false,
      login: async () => ({ success: false, error: 'Not authenticated' }),
      verifyMfaLogin: async () => ({ success: false, error: 'Not authenticated' }),
      resendMfaCode: async () => ({ success: false, error: 'Not authenticated' }),
      loginWithPasskey: async () => ({ success: false, error: 'Not authenticated' }),
      logout: async () => ({ success: false }),
      signup: async () => ({ success: false, error: 'Not authenticated' }),
      checkAuth: async () => {},
      refreshMembership: async () => null,
      markPasswordChanged: () => {}
    };
  }
  return context;
}
